<template>
	<v-dialog v-model="open" max-width="80%" scrollable @click:outside="results ? hide() : null" :persistent="!results">
		<v-card>
			<v-card-title class="title primary--text secondary">
        <translate>TCM Test</translate><span class="mx-1">-</span><translate>Tongue</translate>
      </v-card-title>
			<v-card-text class="py-4">
				<v-row no-gutters>
						<v-col class="px-1">
							<FreehandDrawSVG
								:lineColor="drawProps.lineColor"
								@changeLineColor="changeLineColor"
								@drawSubmit="saveDrawing"
								ref="svgCanvas"
								:test="panel != 1 || template == null ? test.data : template.data"
								:panel="panel"
                :markType="markType"
                :results="results"
                @close-panel="panel = undefined"
								:template="panel == 1 && template != null"
								@set-template="setTemplate"
							>
							</FreehandDrawSVG>
						</v-col>
						<v-col v-if="!results" cols="6" class="px-1">
							<v-expansion-panels v-model="panel" popout focusable>
                <v-expansion-panel key="photos">
									<v-expansion-panel-header>
										<span>
											<v-icon color="primary" class="mr-3">mdi-camera</v-icon>
											<strong><translate>Photos</translate></strong>
										</span>
									</v-expansion-panel-header>
									<v-expansion-panel-content class="pt-5">
										<v-row no-gutters>
                      <template v-if="!results">
                        <v-col v-if="photos.length > 0" cols="12">
                          <v-carousel
                            height="400"
                            hide-delimiter-background
                            show-arrows-on-hover
                          >
                            <v-carousel-item
                              v-for="(image, i) in photos"
                              :key="i">
                              <v-img
                                v-if="image.blob"
                                :src="image.url"
                                height="400"
                                contain
                              />
                              <v-img
                                v-else
                                :src="image.image"
                                height="400"
                                contain
                              />
                            </v-carousel-item>
                          </v-carousel>
                        </v-col>
                        <v-col cols="12" class="text-center">
                          <VueFileAgent
                            ref="vueFileAgent"
                            :theme="'list'"
                            :multiple="true"
                            :deletable="true"
                            :meta="true"
                            :accept="'image/*,.pdf,.doc,.docx'"
                            :maxSize="'50MB'"
                            :maxFiles="10"
                            :helpText="$gettext('Drag or click')"
                            :errorText="{
                              type: 'Invalid file type. Only images and PDFs are allowed',
                              size: 'Files cannot exceed 50MB in size'
                            }"
                            @select="filesSelected($event)"
                            @beforedelete="onBeforeDelete($event)"
                            v-model="fileRecords"
                          ></VueFileAgent>
                        </v-col>
                      </template>
                      <template v-else>
                        <v-col v-if="test.data.photos.length > 0" cols="12">
                          <v-carousel
                            height="400"
                            hide-delimiter-background
                            show-arrows-on-hover
                          >
                            <v-carousel-item
                              v-for="(image, i) in test.data.photos"
                              :key="i">
                              <v-img
                                :src="image.url"
                                height="400"
                                contain
                              />
                            </v-carousel-item>
                          </v-carousel>
                        </v-col>
                      </template>
										</v-row>
									</v-expansion-panel-content>
								</v-expansion-panel>
                <v-expansion-panel key="templates">
									<v-expansion-panel-header>
										<span>
											<v-icon color="primary" class="mr-3">mdi-animation</v-icon>
											<strong><translate>Templates</translate></strong>
										</span>
									</v-expansion-panel-header>
									<v-expansion-panel-content class="pt-5">
										<template v-if="loadingTemplates">
											<v-row no-gutters justify="center" align="center" style="height: 150px">
												<v-progress-circular
													:size="50"
													color="primary"
													indeterminate
												></v-progress-circular>
											</v-row>
										</template>
										<template v-else>
											<!-- Filtros -->
											<v-row v-if="templates.length > 0" no-gutters class="mb-2" justify="center" align="center">
												<v-btn-toggle style="display: block" v-model="template" color="primary" dense group>
													<v-btn v-for="(t, i) in templates" :key="i" :value="t" class="text-overline" min-height="100" :disabled="results">
														<TongueTemplate
															:test="t.data"
														/>
													</v-btn>
												</v-btn-toggle>
											</v-row>
											<p v-else>Nenhum padrão encontrado.</p>
										</template>
									</v-expansion-panel-content>
								</v-expansion-panel>
								<v-expansion-panel key="body">
									<v-expansion-panel-header>
										<span>
											<v-icon color="primary" class="mr-3">$vuetify.icons.tongueIcon</v-icon>
											<strong><translate>Tongue Body</translate></strong>
										</span>
									</v-expansion-panel-header>
									<v-expansion-panel-content class="pt-5">
										<p class="font-weight-bold primary--text"><translate>Shape</translate><span class="font-weight-medium ml-2 grey--text">{{bodyShapeName}}</span></p>
										<v-row no-gutters class="mb-2" justify="center" align="center">
											<v-btn-toggle v-model="test.data.body.shape" mandatory color="primary" dense group>
                        <v-btn value="triangle" class="text-overline" min-height="100" :disabled="results">
													<v-icon size="80" color="primary">$vuetify.icons.tongueTriangleN</v-icon>
												</v-btn>
												<v-btn value="round" class="text-overline" min-height="100" rounded :disabled="results">
													<v-icon size="80" color="primary">$vuetify.icons.tongueRoundN</v-icon>
												</v-btn>
												<v-btn value="square" class="text-overline" min-height="100" :disabled="results">
													<v-icon size="80" color="primary">$vuetify.icons.tongueSquareN</v-icon>
												</v-btn>
											</v-btn-toggle>
										</v-row>
										<p class="font-weight-bold primary--text"><translate>Size</translate><span class="font-weight-medium ml-2 grey--text">{{bodySizeName}}</span></p>
										<v-row no-gutters class="mb-2" justify="center" align="center">
											<v-btn-toggle v-model="test.data.body.size" mandatory color="primary" dense group>
												<template v-if="test.data.body.shape == 'round'">
													<v-btn value="small" class="text-overline" min-height="100" rounded :disabled="results">
														<v-icon size="80" color="primary">$vuetify.icons.tongueRoundS</v-icon>
													</v-btn>
													<v-btn value="normal" class="text-overline" min-height="100" rounded :disabled="results">
														<v-icon size="80" color="primary">$vuetify.icons.tongueRoundN</v-icon>
													</v-btn>
													<v-btn value="large" class="text-overline" min-height="100" rounded :disabled="results">
														<v-icon size="80" color="primary">$vuetify.icons.tongueRoundL</v-icon>
													</v-btn>
												</template>
												<template v-else-if="test.data.body.shape == 'square'">
													<v-btn value="small" class="text-overline" min-height="100" rounded :disabled="results">
														<v-icon size="80" color="primary">$vuetify.icons.tongueSquareS</v-icon>
													</v-btn>
													<v-btn value="normal" class="text-overline" min-height="100" rounded :disabled="results">
														<v-icon size="80" color="primary">$vuetify.icons.tongueSquareN</v-icon>
													</v-btn>
													<v-btn value="large" class="text-overline" min-height="100" rounded :disabled="results">
														<v-icon size="80" color="primary">$vuetify.icons.tongueSquareL</v-icon>
													</v-btn>
												</template>
												<template v-else-if="test.data.body.shape == 'triangle'">
													<v-btn value="small" class="text-overline" min-height="100" rounded :disabled="results">
														<v-icon size="80" color="primary">$vuetify.icons.tongueTriangleS</v-icon>
													</v-btn>
													<v-btn value="normal" class="text-overline" min-height="100" rounded :disabled="results">
														<v-icon size="80" color="primary">$vuetify.icons.tongueTriangleN</v-icon>
													</v-btn>
													<v-btn value="large" class="text-overline" min-height="100" rounded :disabled="results">
														<v-icon size="80" color="primary">$vuetify.icons.tongueTriangleL</v-icon>
													</v-btn>
												</template>
											</v-btn-toggle>
										</v-row>
										<p class="font-weight-bold mb-0 primary--text"><translate>Tension</translate><span class="font-weight-medium ml-2 grey--text">{{bodyTensionName}}</span></p>
										<v-row>
											<v-col class="pa-5 pt-3">
												<v-slider
													:tick-labels="bodyTensionOptions"
													v-model="test.data.body.tension"
													min="0"
													max="4"
													ticks
													tick-size="2"
													track-color="secondary"
													hide-details
													class="text-caption"
													:disabled="results"
												>
												</v-slider>
											</v-col>
										</v-row>
										<p class="font-weight-bold mb-0 mt-3 primary--text"><translate>Humidity</translate><span class="font-weight-medium ml-2 grey--text">{{bodyHumidityName}}</span></p>
										<v-row>
											<v-col class="pa-5 pt-3">
												<v-slider
													:tick-labels="bodyHumidityOptions"
													v-model="test.data.body.humidity"
													min="0"
													max="4"
													ticks
													tick-size="2"
													track-color="secondary"
													hide-details
													class="text-caption"
													:disabled="results"
												>
												</v-slider>
											</v-col>
										</v-row>
									</v-expansion-panel-content>
								</v-expansion-panel>
								<v-expansion-panel key="cover">
									<v-expansion-panel-header>
										<span>
											<v-icon color="primary" class="mr-3">$vuetify.icons.tongueIcon</v-icon>
											<strong><translate>Cover</translate></strong>
										</span>
									</v-expansion-panel-header>
									<v-expansion-panel-content class="pt-5">
										<v-row no-gutters align="center" class="mb-2">
											<v-checkbox
												v-model="test.data.cover.exist"
												hide-details
												class="shrink mr-2 mt-0"
												:label="$gettext('Exist')"
												:disabled="results"
											></v-checkbox>
										</v-row>
										<v-radio-group v-model="test.data.cover.area" row v-if="test.data.cover.exist" :disabled="results">
											<v-radio value="total">
												<template v-slot:label>
													Total
												</template>
											</v-radio>
											<v-radio value="partial">
												<template v-slot:label>
													<translate>Partial</translate>
												</template>
											</v-radio>
										</v-radio-group>
										<v-row no-gutters v-if="test.data.cover.exist">
											<p class="font-weight-bold mb-0 primary--text"><translate>Quality</translate></p>
											<v-col cols="12" class="px-5">
												<v-select
													v-model="test.data.cover.quality"
													:items="coverQualities"
													item-text="name"
													item-value="code"
													hide-details
													:disabled="results"
												></v-select>
											</v-col>
											<p class="font-weight-bold mb-0 mt-4 primary--text"><translate>Thickness</translate><span class="font-weight-medium ml-2 grey--text">{{coverThicknessName}}</span></p>
											<v-col cols="12">
												<v-row no-gutters>
													<v-col cols="12" class="pa-5 pt-3">
														<v-slider
															:tick-labels="coverThicknessOptions"
															v-model="test.data.cover.thickness"
															min="0"
															max="2"
															ticks
															tick-size="2"
															track-color="secondary"
															hide-details
															class="text-caption"
															:disabled="results"
														>
														</v-slider>
													</v-col>
												</v-row>
											</v-col>
										</v-row>
									</v-expansion-panel-content>
								</v-expansion-panel>
								<v-expansion-panel key="marks">
									<v-expansion-panel-header>
										<span>
											<v-icon color="primary" class="mr-3">mdi-format-line-style</v-icon>
											<strong><translate>Marks</translate></strong>
										</span>
									</v-expansion-panel-header>
									<v-expansion-panel-content class="pt-5">
                    <v-row no-gutters align="center" class="mb-2">
											<v-checkbox
												v-model="test.data.marks.exist"
												hide-details
												class="shrink mr-2 mt-0"
												:label="$gettext('Exist')"
												:disabled="results"
											></v-checkbox>
										</v-row>
                    <v-radio-group v-model="markType" v-if="test.data.marks.exist" :disabled="results">
											<v-radio value="grooves">
												<template v-slot:label>
													<translate>Grooves</translate>
													<v-icon class="ml-2" small v-if="markType == 'grooves'">mdi-lead-pencil</v-icon>
                          <v-spacer></v-spacer>
													<!-- <v-btn small v-if="markType == 'grooves'" color="primary" icon @click="removeMarks('grooves')">
														<v-icon>mdi-delete-forever-outline</v-icon>
													</v-btn> -->
                          <v-checkbox
                            v-model="test.data.marks.grooves"
                            hide-details
                            class="shrink mr-2 mt-0"
                          ></v-checkbox>
												</template>
											</v-radio>
                      <v-radio value="spots">
												<template v-slot:label>
													<translate>Spots</translate>
													<v-icon class="ml-2" small v-if="markType == 'spots'">mdi-lead-pencil</v-icon>
                          <v-spacer></v-spacer>
													<!-- <v-btn small v-if="markType == 'spots'" color="primary" icon @click="removeMarks('spots')">
														<v-icon>mdi-delete-forever-outline</v-icon>
													</v-btn> -->
                          <v-checkbox
                            v-model="test.data.marks.spots"
                            hide-details
                            class="shrink mr-2 mt-0"
                          ></v-checkbox>
												</template>
											</v-radio>
                      <v-radio value="teeth">
												<template v-slot:label>
                          <translate>Teeth</translate>
													<v-icon class="ml-2" small v-if="markType == 'teeth'">mdi-lead-pencil</v-icon>
                          <v-spacer></v-spacer>
													<!-- <v-btn small v-if="markType == 'teeth'" color="primary" icon @click="removeMarks('teeth')">
														<v-icon>mdi-delete-forever-outline</v-icon>
													</v-btn> -->
                          <v-checkbox
                            v-model="test.data.marks.teeth"
                            hide-details
                            class="shrink mr-2 mt-0"
                          ></v-checkbox>
												</template>
											</v-radio>
										</v-radio-group>
									</v-expansion-panel-content>
								</v-expansion-panel>
								<v-expansion-panel key="movement">
									<v-expansion-panel-header>
										<span>
											<v-icon color="primary" class="mr-3">mdi-axis-arrow</v-icon>
											<strong><translate>Movement</translate></strong>
										</span>
									</v-expansion-panel-header>
									<v-expansion-panel-content class="pt-2">
										<v-radio-group v-model="test.data.movement.type" row :disabled="results">
											<v-radio value="left">
												<template v-slot:label>
													<translate>Left Deviation</translate>
												</template>
											</v-radio>
											<v-radio value="inexistent">
												<template v-slot:label>
													<translate>Inexistent</translate>
												</template>
											</v-radio>
											<v-radio value="right">
												<template v-slot:label>
													<translate>Right Deviation</translate>
												</template>
											</v-radio>
										</v-radio-group>
                    <v-checkbox
											v-model="test.data.movement.trembling"
											hide-details
											class="shrink mr-2 mt-0"
											:label="$gettext('Trembling')"
											:disabled="results"
										></v-checkbox>
									</v-expansion-panel-content>
								</v-expansion-panel>
								<v-expansion-panel key="veins">
									<v-expansion-panel-header>
										<span>
											<v-icon color="primary" class="mr-3">check</v-icon>
											<strong><translate>Sublingual veins</translate></strong>
										</span>
									</v-expansion-panel-header>
									<v-expansion-panel-content class="pt-3">
										<v-row no-gutters align="center" class="mb-3">
											<v-radio-group v-model="test.data.veins.type" row hide-details :disabled="results">
												<v-radio value="normal">
													<template v-slot:label>
														<translate>Normal</translate>
													</template>
												</v-radio>
												<v-radio value="changed">
													<template v-slot:label>
														<translate>Changed</translate>
													</template>
												</v-radio>
											</v-radio-group>
										</v-row>
										<v-row v-if="test.data.veins.type != 'normal'" no-gutters>
											<p class="font-weight-bold mb-0 mt-4 primary--text"><translate>Thickness</translate><span class="font-weight-medium ml-2 grey--text">{{veinsThicknessName}}</span></p>
											<v-col cols="12">
												<v-row no-gutters>
													<v-col cols="12" class="pa-5 pt-3">
														<v-slider
															:tick-labels="coverThicknessOptions"
															v-model="test.data.veins.thickness"
															min="0"
															max="2"
															ticks
															tick-size="2"
															track-color="secondary"
															hide-details
															class="text-caption"
															:disabled="results"
														>
														</v-slider>
													</v-col>
												</v-row>
											</v-col>
											<p class="font-weight-bold mb-0 mt-4 primary--text"><translate>Tortuosity</translate><span class="font-weight-medium ml-2 grey--text">{{veinsTortuosityName}}</span></p>
											<v-col cols="12">
												<v-row no-gutters>
													<v-col cols="12" class="pa-5 pt-3">
														<v-slider
															:tick-labels="veinsTortuosityOptions"
															v-model="test.data.veins.tortuosity"
															min="0"
															max="2"
															ticks
															tick-size="2"
															track-color="secondary"
															hide-details
															class="text-caption"
															:disabled="results"
														>
														</v-slider>
													</v-col>
												</v-row>
											</v-col>
											<!-- <v-col cols="6">
												<v-select
													v-model="test.data.veins.thickness"
													:items="veinsThicknessOptions"
													:label="$gettext('Thickness')"
													item-text="name"
													item-value="code"
													hide-details
													v-if="test.data.veins.type != 'normal'"
													class="px-1"
													:disabled="results"
												></v-select>
											</v-col> -->
											<!-- <v-col cols="6">
												<v-select
													v-model="test.data.veins.tortuosity"
													:items="veinsTortuosities"
													:label="$gettext('Tortuosity')"
													item-text="name"
													item-value="code"
													hide-details
													v-if="test.data.veins.type != 'normal'"
													class="px-1"
													:disabled="results"
												></v-select>
											</v-col> -->
										</v-row>
									</v-expansion-panel-content>
								</v-expansion-panel>
								<v-expansion-panel key="notes">
									<v-expansion-panel-header>
										<span>
											<v-icon color="primary" class="mr-3">mdi-note-multiple</v-icon>
											<strong><translate>Notes</translate></strong>
										</span>
									</v-expansion-panel-header>
									<v-expansion-panel-content class="pt-5">
										<v-row no-gutters>
											<v-col cols="12" class="text-center">
												<v-textarea v-model="test.data.note" :label="$gettext('Note')" class="px-2" outlined :disabled="results" />
											</v-col>
										</v-row>
									</v-expansion-panel-content>
								</v-expansion-panel>
							</v-expansion-panels>
						</v-col>
				</v-row>
			</v-card-text>
			<v-divider></v-divider>
			<v-card-actions>
				<v-btn v-if="results" color="primary darken-1" text @click="hide">
					<translate>Close</translate>
				</v-btn>
				<v-btn v-else color="primary darken-1" text @click="hide">
					<translate>Cancel</translate>
				</v-btn>
				<v-spacer></v-spacer>
				<v-btn v-if="!edit && !results" color="primary darken-1" text @click="submitTest">
					<translate>Create</translate>
				</v-btn>
				<v-btn v-if="edit" color="primary darken-1" text @click="editTest">
					<translate>Save</translate>
				</v-btn>
				<v-btn v-if="results" color="primary darken-1" text @click="$emit('open-edit')">
					<translate>Edit</translate>
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import FreehandDrawSVG from "@/components/drawing/FreehandDrawSVG.vue";
import TongueTemplate from "@/components/drawing/TongueTemplate.vue";
import axios from "axios";
import { mapGetters } from 'vuex'

export default {
	name: "TCMTongueForm",
	components: {
		FreehandDrawSVG,
		TongueTemplate
	},
	props: {
		test: {
			default: () => ({
				kind: "exam",
				module: "tcm",
				data: {
          photos: [],
					body: {
						shape: 'round',
						size: 'normal',
						tension: 2,
						humidity: 2,
						parts: {
							'body' :{
								fill: 'rgb(245,154,154,1)',
								filter: null
							},
							'k-bl-li': {
								fill: 'rgb(245,154,154,1)',
								filter: null
							},
							'lv-gb-r': {
								fill: 'rgb(245,154,154,1)',
								filter: null
							},
							'lv-gb-l': {
								fill: 'rgb(245,154,154,1)',
								filter: null
							},
							'lg': {
								fill: 'rgb(245,154,154,1)',
								filter: null
							},
							'he': {
								fill: 'rgb(245,154,154,1)',
								filter: null
							},
							'st-sp': {
								fill: 'rgb(245,154,154,1)',
								filter: null
							}
						},
						elements: []
					},
					cover: {
						exist: false,
						area: 'total',
						quality: 'normal',
						thickness: 0,
						parts: {
							'cover': {
								fill: 'rgba(255, 255, 255, 0)',
								filter: 'url(#blur)'
							}
						},
						elements: []
					},
					marks: {
            exist: false,
            grooves: false,
            teeth: false,
            spots: false,
						elements: []
					},
					movement: {
            type: 'inexistent',
            trembling: false
          },
					veins: {
						type: 'normal',
						thickness: 1,
						tortuosity: 1
					},
					note: ""
				}
			})
		},
		edit: {
			default: false
		},
		results: {
			default: false
		}
	},
	data: () => ({
    open: true,
		drawProps: {
      lineColor: "#292929"
		},
    panel: 0,
    markType: 'grooves',
    fileRecords: [],
		fileRecordsForUpload: [],
		loadingTemplates: false,
		templates: [],
		template: null
	}),
	computed: {
		coverQualities() {
			return [
				{ name: this.$gettext('Normal'), code: "normal" },
				{ name: this.$gettext('Sticky'), code: "sticky" },
				{ name: this.$gettext('Geographic'), code: "geographic" }
			]
		},
		coverThicknessOptions() {
			return [
				this.$gettext('Slim'),
				this.$gettext('Medium'),
				this.$gettext('Thick')
			]
		},
		veinsThicknessOptions() {
			return [
				{ name: this.$gettext('Slim'), code: "slim" },
				{ name: this.$gettext('Medium'), code: "medium" },
				{ name: this.$gettext('Thick'), code: "thick" }
			]
		},
		veinsTortuosityOptions() {
			return [
				this.$gettext('Light'),
				this.$gettext('Moderate'),
				this.$gettext('Sharp')
			]
		},
		bodyTensionOptions() {
			return [
				this.$gettext('Flaccid') + ' ++',
				'',
				this.$gettext('Normal'),
				'',
				this.$gettext('Rigid') + ' ++'
			]
		},
		bodyHumidityOptions() {
			return [
				this.$gettext('Dry') + ' ++',
				'',
				this.$gettext('Normal'),
				'',
				this.$gettext('Wet') + ' ++'
			]
		},
    photos() {
      return this.fileRecordsForUpload.concat(this.test.data.photos);
		},
		bodyShapeName() {
			if(this.test.data.body.shape == 'round') return this.$gettext('Round');
			if(this.test.data.body.shape == 'triangle') return this.$gettext('Triangular');
			return this.$gettext('Square');
		},
		bodySizeName() {
			if(this.test.data.body.size == 'small') return this.$gettext('Small');
			if(this.test.data.body.size == 'normal') return this.$gettext('Normal');
			return this.$gettext('Large');
		},
		bodyHumidityName() {
			if(this.test.data.body.humidity == 0) return this.$gettext('Extremely Dry');
			if(this.test.data.body.humidity == 1) return this.$gettext('Dry');
			if(this.test.data.body.humidity == 2) return this.$gettext('Normal');
			if(this.test.data.body.humidity == 3) return this.$gettext('Wet');
			return this.$gettext('Extremely Wet');
		},
		bodyTensionName() {
			if(this.test.data.body.tension == 0) return this.$gettext('Extremely Flaccid');
			if(this.test.data.body.tension == 1) return this.$gettext('Flaccid');
			if(this.test.data.body.tension == 2) return this.$gettext('Normal');
			if(this.test.data.body.tension == 3) return this.$gettext('Rigid');
			return this.$gettext('Extremely Rigid');
		},
		coverThicknessName() {
			if(this.test.data.cover.thickness == 0) return this.$gettext('Slim');
			if(this.test.data.cover.thickness == 1) return this.$gettext('Medium');
			return this.$gettext('Thick');
		},
		veinsThicknessName() {
			if(this.test.data.veins.thickness == 0) return this.$gettext('Slim');
			if(this.test.data.veins.thickness == 1) return this.$gettext('Medium');
			return this.$gettext('Thick');
		},
		veinsTortuosityName() {
			if(this.test.data.veins.tortuosity == 0) return this.$gettext('Light');
			if(this.test.data.veins.tortuosity == 1) return this.$gettext('Moderate');
			return this.$gettext('Sharp');
		},
		...mapGetters({
      tourStep: 'tourStepEvent'
    })
	},
	methods: {
		changeLineColor(color) {
      this.drawProps.lineColor = color;
    },
    saveDrawing(drawSvg) {
      console.log (drawSvg)      
    },
    filesSelected(fileRecordsNewlySelected) {
      for(let i = 0; i < fileRecordsNewlySelected.length; i++) {
        fileRecordsNewlySelected[i].image = URL.createObjectURL(fileRecordsNewlySelected[i].file);
      }
      var validFileRecords = fileRecordsNewlySelected.filter((fileRecord) => !fileRecord.error);
      this.fileRecordsForUpload = this.fileRecordsForUpload.concat(validFileRecords);
		},
		onBeforeDelete(fileRecord) {
			var h = this.fileRecords.indexOf(fileRecord);
      if (h !== -1) {
				this.fileRecords.splice(h, 1);
			}
			var i = this.fileRecordsForUpload.indexOf(fileRecord);
      if (i !== -1) {
				this.fileRecordsForUpload.splice(i, 1);
			}
    },
		submitTest() {
      this.$refs.svgCanvas.saveState();
			const formData = new FormData();
			formData.append("info", JSON.stringify(this.test.data));
			formData.append("kind", "tongue");
      formData.append("module", "tcm");
      for(let i = 0; i < this.fileRecordsForUpload.length; i++) {
        formData.append("file " + i, this.fileRecordsForUpload[i].file);
      }
			this.$emit("create-test", formData);
			this.hide();
		},
		editTest() {
      this.$refs.svgCanvas.saveState();
			const formData = new FormData();
			formData.append("info", JSON.stringify(this.test.data));
			formData.append("kind", "tongue");
      formData.append("module", "tcm");
      for(let i = 0; i < this.fileRecordsForUpload.length; i++) {
        formData.append("file " + i, this.fileRecordsForUpload[i].file);
      }
			this.$emit("edit-test", formData);
			this.hide();
		},
		hide() {
			this.$emit("hide");
		},
		removeMarks(kind) {
			this.$refs.svgCanvas.removeMarks(kind);
			if(kind == 'grooves') this.test.data.marks.grooves = false;
			else if(kind == 'spots') this.test.data.marks.spots = false;
			else this.test.data.marks.teeth = false;
		},
		getTemplates() {
			this.loadingTemplates = true;
			axios
				.get("/tongue-templates")
				.then(response => {
					console.log(response.data);
					this.templates = response.data.tongues;
					this.loadingTemplates = false;
				})
				.catch(error => {
					console.log(error);
					this.loadingTemplates = false;
				});
		},
		setTemplate() {
			console.log("SET TEMPLATE");
			let photos = JSON.parse(JSON.stringify(this.test.data.photos));
			this.test.data = JSON.parse(JSON.stringify(this.template.data));
			this.test.data.photos = photos;
			this.panel = null;
			this.template = null;
		}
	},
	watch: {
		template(newvalue, oldValue) {
			console.log("CHANGE TEMPLATE", newvalue);
			if(oldValue == null) {
				console.log("SAVE NOW");
				this.$refs.svgCanvas.saveState();
			}
		},
		panel(value) {
			if(value == 1) {
				this.template = null;
				this.getTemplates();
			}
		},
		tourStep(value) {
			if(value == 'tour-test-tongue-open-templates') {
				this.panel = 1;
			}
			else if(value == 'tour-test-tongue-select-template') {
				this.template = this.templates[2];
				this.setTemplate();
			}
			else if(value == 'tour-test-tongue-submit') {
				this.submitTest();
			}
		}
	}
};
</script>